import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild} from '@angular/core';
import {MatDialog} from "@angular/material/dialog";
import {IBaseBlockComponent, IEventBlock, IPage, ISendEvents} from "../../../../interfaces";
import {TranslocoService} from "@ngneat/transloco";
import {BlockBaseComponent} from "../block-base/block-base.component";
import {actionAddSceneItem, actionUpdateEventsStrings} from "../../../../store/actions/user.actions";
import {ButtonBarComponent, IButtonBar} from "../../../../components/add-remove-bar/button-bar.component";
import {StoreSelectorsService} from "../../../../services";
import {Memoizee} from "../../../../decorators/memoizee-decorator";
import {animate, state, style, transition, trigger} from '@angular/animations';
import {DialogEventsExplorerComponent} from "../../../events/dialog-events-explorer/dialog-events-explorer.component";
import {take} from "rxjs/operators";

@Component({
    selector: 'app-block-receive-events',
    templateUrl: './block-receive-events.component.html',
    styleUrls: ['./block-receive-events.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('detailExpand', [
            state('collapsed, void', style({ height: '0px' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
            transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
        ]),
    ],
    standalone: false
})
export class BlockReceiveEventsComponent extends BlockBaseComponent implements IBaseBlockComponent {

    dataSource: Array<any> = [];
    displayedColumns: string[] = ['from', 'command'];
    displayedColumnsWithEdit = [...this.displayedColumns, 'editIcon'];
    commandOptions = [];
    dropdownSelect: Array<IPage> = [];
    // account scenes offered as the openPopup target (value = scene id, label = scene name)
    sceneOptions: Array<{value: string, label: string}> = [];
    optionsLib = [];
    highlightedRows = [];
    buttons: IButtonBar[] = [];
    eventsAll: Array<string> = [];
    eventsAllFiltered: Array<string> = [];
    expandedElement = null;
    expandedIndex = null;

    @ViewChild('dtable') dtable: any;
    @ViewChild(ButtonBarComponent) addRemoveBarComponent: ButtonBarComponent;

    constructor(private translocoService: TranslocoService,
                private cd: ChangeDetectorRef,
                public override storeSelector: StoreSelectorsService,
                public override dialog: MatDialog) {
        super(storeSelector, dialog);
        this.commandOptions = [];
        this.optionsLib = [
            {name: 'First', value: 'firstPage'},
            {name: 'Prev', value: 'prevPage'},
            {name: 'Next', value: 'nextPage'},
            {name: 'Last', value: 'lastPage'},
            {name: 'Fixed', value: 'selectPage'},
            {name: 'By Param', value: 'paramPage'},
            {name: 'Filter', value: 'filter'},
            {name: 'Load', value: 'load'},
            {name: 'Clear', value: 'clear'},
            {name: 'Submit', value: 'save'},
            {name: 'Update', value: 'update'},
            {name: 'Load Package', value: 'loadPackage'},
            {name: 'Load Content', value: 'loadPackageContent'},
            {name: 'Remove Selected', value: 'removeSelected'},
            {name: 'Send Key', value: 'sendKey'},
            {name: 'Keyboard', value: 'selectKeyboard'},
            {name: 'Add String', value: 'addParamLine'},
            {name: 'Set String', value: 'setParam'},
            {name: 'Dec String', value: 'decParam'},
            {name: 'Add Json', value: 'addParamFieldLine'},
            {name: 'On/Off', value: 'paramOnOff'},
            {name: 'Level', value: 'paramLevel'},
            {name: 'Color', value: 'paramColor'},
            {name: 'Trigger', value: 'trigger'},
            {name: 'Arm', value: 'arm'},
            {name: 'Open Popup', value: 'openPopup'},
            {name: 'Close Popup', value: 'closePopup'},
        ];
        this.buttons = [
            {
                callBack: v => (v),
                icon: 'add',
                name: 'add',
                disableByList: false,
                disabled: false,
                tooltip: '+'
            },
            {
                callBack: v => (v),
                icon: 'remove',
                name: 'remove',
                confirmRemove: this.translocoService.translate('toast.deleteConfirm', {}, this.translocoService.getActiveLang()),
                disableByList: true,
                disabled: false,
                tooltip: '-'
            },
        ];
        this.cod(
            this.storeSelector.getEventsCombined().subscribe((events) => {
                this.eventsAll = this.eventsAllFiltered = events;
            })
        );
        // scenes available as popup targets (openPopup). Exclude the scene being edited to avoid self-nesting.
        this.cod(
            this.storeSelector.getScenes().subscribe((scenes) => {
                this.sceneOptions = (scenes || [])
                    .filter((s) => s?.Player?.$?.id != null && String(s.Player.$.id) !== String(this.sceneId))
                    .map((s) => ({value: String(s.Player.$.id), label: s.Player.$.label || ('scene ' + s.Player.$.id)}));
            })
        );
    }

    getSendEventTemplate(): ISendEvents {
        switch (this.sceneItem.$.player) {
            case this.blockLabelRecords.COLLECTION.id:
            case this.blockLabelRecords.MUSIC.id:
            case this.blockLabelRecords.PLAYLIST.id: {
                return {
                    $: {
                        from: '',
                        command: '',
                    },
                    Params: [
                        {
                            Page: [
                                {
                                    $: {
                                        name: '',
                                        page: '',
                                        type: ''
                                    },
                                    Player: [
                                        {
                                            $: {}
                                        }
                                    ]
                                }
                            ]
                        }
                    ]
                };
            }
            case this.blockLabelRecords.BLOCKCODE_SCENE.id: {
                // openPopup stores the target scene id in Params[0].Page[0].$.name (reusing the Page slot,
                // exactly like selectPage) so setParam/pageValueChanged persist it with no special plumbing.
                return {
                    $: {
                        from: '',
                        command: '',
                    },
                    Params: [
                        {
                            Page: [
                                {
                                    $: {
                                        name: '',
                                        page: '',
                                        type: ''
                                    },
                                    Player: [
                                        {
                                            $: {}
                                        }
                                    ]
                                }
                            ]
                        }
                    ]
                };
            }
            case this.blockLabelRecords.CHAT.id: {
                return {
                    $: {
                        from: '',
                        command: '',
                    },
                    Params: [
                        {
                            Field: [
                                {
                                    $: {
                                        name: ''
                                    }
                                }
                            ]
                        }
                    ]
                };
            }
            default: {
                return {
                    $: {
                        from: '',
                        command: '',
                    },
                    Params: [
                        {
                            Param: [
                                {
                                    $: {
                                        broadcast: ''
                                    }
                                }
                            ]
                        }
                    ]
                };
            }
        }
    }

    override preInit() {
        super.preInit();
    }

    onFilter(event) {
        this.eventsAllFiltered = this.eventsAll.filter((a) => {
            if (!a || event?.target?.value) return false;
            return a.toLowerCase().indexOf(event.target.value.toLowerCase()) > -1;
        });
    }

    onCustomizeTableDisplayColumns() {
        if (this.displayedColumns.length === 3) {
            this.displayedColumns.pop();
        }
        switch (this.sceneItem.$.player) {
            case this.blockLabelRecords.CATALOG.id: {
                this.displayedColumns.push('select');
                this.onAddOptionsFromLib(['firstPage', 'prevPage', 'nextPage', 'lastPage']);
                this.dropdownSelect = this.sceneItem.Data[0].Catalog[0].Page;
                break;
            }
            case this.blockLabelRecords.CUSTOMRSS.id: {
                this.displayedColumns.push('select');
                this.onAddOptionsFromLib(['firstPage', 'prevPage', 'nextPage', 'lastPage']);
                this.dropdownSelect = this.sceneItem.Data[0].Rss[0].CustomRss[0].Page;
                break;
            }
            case this.blockLabelRecords.STOCKS.id: {
                this.displayedColumns.push('select');
                this.onAddOptionsFromLib(['firstPage', 'prevPage', 'nextPage', 'lastPage']);
                this.dropdownSelect = this.sceneItem.Data[0].Stocks[0].Page;
                break;
            }
            case this.blockLabelRecords.XML.id: {
                this.displayedColumns.push('select');
                this.onAddOptionsFromLib(['firstPage', 'prevPage', 'nextPage', 'lastPage']);
                this.dropdownSelect = this.sceneItem.Data[0].Xml[0].Page;
                break;
            }
            case this.blockLabelRecords.COLLECTION.id: {
                this.displayedColumns.push('select');
                this.onAddOptionsFromLib(['firstPage', 'prevPage', 'nextPage', 'lastPage', 'selectPage', 'paramPage']);
                this.dropdownSelect = this.sceneItem.Data[0].Collection[0].Page;
                break;
            }
            case this.blockLabelRecords.MUSIC.id:
            case this.blockLabelRecords.PLAYLIST.id: {
                this.displayedColumns.push('select');
                this.onAddOptionsFromLib(['firstPage', 'prevPage', 'nextPage', 'lastPage', 'selectPage', 'paramPage']);
                this.dropdownSelect = this.sceneItem.Data[0].Playlist[0].Page;
                break;
            }
            case this.blockLabelRecords.FLICKING.id: {
                this.displayedColumns.push('select');
                this.onAddOptionsFromLib(['firstPage', 'prevPage', 'nextPage', 'lastPage']);
                this.dropdownSelect = this.sceneItem.Data[0].Flicking[0].Page;
                break;
            }
            case this.blockLabelRecords.JSON_VIEWER.id: {
                this.displayedColumns.push('broadcast');
                this.onAddOptionsFromLib(['load', 'save', 'update']);
                break;
            }
            case this.blockLabelRecords.JSON_PLAYER.id: {
                this.onAddOptionsFromLib(['firstPage', 'prevPage', 'nextPage', 'lastPage', 'filter', 'load', 'save']);
                break;
            }
            case this.blockLabelRecords.CHAT.id: {
                this.displayedColumns.push('field');
                this.onAddOptionsFromLib(['setParam', 'addParamLine', 'addParamFieldLine']);
                break;
            }
            case this.blockLabelRecords.CART.id: {
                this.displayedColumns.push('broadcast');
                this.onAddOptionsFromLib(['load', 'save', 'removeSelected']);
                break;
            }
            case this.blockLabelRecords.LABEL.id: {
                this.onAddOptionsFromLib(['setParam', 'addParamLine', 'decParam']);
                break;
            }
            case this.blockLabelRecords.BULB.id: {
                this.onAddOptionsFromLib(['paramOnOff', 'paramLevel', 'paramColor']);
                break;
            }
            case this.blockLabelRecords.ALARM_SYS.id: {
                this.displayedColumns.push('zone');
                this.onAddOptionsFromLib(['trigger', 'arm']);
                break;
            }
            case this.blockLabelRecords.GAUGE.id:
            case this.blockLabelRecords.KNOB.id:
            case this.blockLabelRecords.TRIVIA.id:
            case this.blockLabelRecords.COLOR_CONTROL.id: {
                this.onAddOptionsFromLib(['setParam']);
                break;
            }
            case this.blockLabelRecords.SWITCH.id: {
                this.onAddOptionsFromLib(['paramOnOff']);
                break;
            }
            case this.blockLabelRecords.LOADER.id: {
                this.onAddOptionsFromLib(['load', 'clear']);
                break;
            }
            case this.blockLabelRecords.BLOCKCODE_SCENE.id: {
                // top-level (mainPlayer) scene only — gated to player-assignable in scene-editor.
                // openPopup carries a target scene (select column); closePopup needs none.
                this.displayedColumns.push('select');
                this.onAddOptionsFromLib(['openPopup', 'closePopup']);
                break;
            }
            case this.blockLabelRecords.SCHEDULER.id: {
                this.onAddOptionsFromLib(['load']);
                break;
            }
            case this.blockLabelRecords.HOURLYBALANCER.id: {
                this.onAddOptionsFromLib(['load']);
                break;
            }
            default: {
                this.commandOptions = this.optionsLib;
                break;
            }
        }
        this.displayedColumnsWithEdit = [...this.displayedColumns, 'editIcon'];
    }

    onAddOptionsFromLib(optionsAdd: Array<string>) {
        this.commandOptions = this.optionsLib.filter((v) => {
            return optionsAdd.indexOf((v.value)) > -1;
        });
    }

    onAdd(v) {
        const template = this.getSendEventTemplate();

        template.$.from = 'from';
        template.$.command = 'command';
        if (this.displayedColumns.indexOf('zone') > -1) {
            template.$.zone = '0';
        }
        if (template.Params[0].Param) {
            template.Params[0].Param[0].$.broadcast = '0';
        } else if (template.Params[0].Field) {
            template.$.command = 'setParam';
            template.Params[0].Field[0].$.name = 'field';
        } else {
            template.Params[0].Page[0].$.name = 'Page';
        }

        if (this.sceneItem.Data[0].EventCommands) {
            this.storeSelector.dispatch(actionAddSceneItem({
                sceneId: this.sceneId,
                sceneItemId: this.sceneItem.$.id,
                values: [{
                    key: 'EventCommand',
                    value: template,
                    storePath: `Data[0].EventCommands[0]`
                }]
            }));
        } else {
            this.dispatchSceneItemChanges({
                sceneId: this.sceneId,
                sceneItemId: this.sceneItem.$.id,
                values: [{
                    key: 'EventCommands',
                    value: [{"EventCommand": [template]}],
                    storePath: `Data[0]`
                }]
            });
        }
    }

    onRemove(v) {
        if (this.highlightedRows.length === 0) return;
        const values: ISendEvents[] = [];
        this.highlightedRows.forEach((i_selected) => {
            this.dataSource.forEach((item: ISendEvents, index) => {
                if (item === i_selected) {
                    return;
                }
                const value = this.getSendEventTemplate();
                value.$.from = item.$.from;
                value.$.command = item.$.command;
                value.$.zone = item.$.zone;
                this.setParam(value, item);
                values.push(value);
            });
        });
        this.dispatchChanges(values);
    }

    dispatchEventsAll(eventsAll) {
        this.storeSelector.dispatch(actionUpdateEventsStrings({eventsStrings: eventsAll}));
    }

    broadcastChanged(index: number, i_value) {
        const values: Array<ISendEvents> = [];
        this.dataSource.forEach((item: ISendEvents, counter) => {
            const value = this.getSendEventTemplate();
            value.$.from = item.$.from;
            value.$.command = item.$.command;
            value.$.zone = item.$.zone;
            value.Params[0].Param[0].$.broadcast = counter === index ? (i_value.Params[0].Param[0].$.broadcast === '0' ? '1' : '0') : item.Params[0].Param[0].$.broadcast;
            values.push(value);
        });
        this.dispatchChanges(values);
    }

    dropdownOnSelectionChanged(event, i_item) {
        const values: Array<ISendEvents> = [];
        this.dataSource.forEach((item: ISendEvents, index) => {
            const value = this.getSendEventTemplate();
            value.$.from = item.$.from;
            value.$.command = item === i_item ? event.value : item.$.command;
            value.$.zone = item.$.zone;
            this.setParam(value, item);
            values.push(value);
        });
        this.dispatchChanges(values);
    }

    zoneValueChanged(index: number, event, i_item) {
        const values: Array<ISendEvents> = [];
        this.dataSource.forEach((item: ISendEvents) => {
            const value = this.getSendEventTemplate();
            value.$.from = item.$.from;
            value.$.command = item.$.command;
            value.$.zone = item === i_item ? event.value : item.$.zone;
            this.setParam(value, item);
            values.push(value);
        });
        this.dispatchChanges(values);
    }

    pageValueChanged(index, event, i_item) {
        const values: Array<ISendEvents> = [];
        this.dataSource.forEach((item: ISendEvents, i) => {
            const template = this.getSendEventTemplate();
            template.$.from = item.$.from;
            template.$.command = item === i_item ? i_item.$.command : item.$.command;
            template.$.zone = item.$.zone;
            template.Params[0].Page[0].$.name = item?.Params[0].Page[0].$.name;
            values.push(template);
        });
        this.dispatchChanges(values);
    }

    inputEdited(col, index: number, i_value, isCollapse = false) {
        const values: Array<ISendEvents> = [];
        this.dataSource.forEach((item: ISendEvents, counter) => {
            const template = this.getSendEventTemplate();
            template.$.from = (col === 'from' && counter === index) ? i_value : item.$.from;
            template.$.command = item.$.command;
            template.$.zone = item.$.zone;
            if (col === 'field') {
                template.Params[0].Field[0].$.name = counter === index ? i_value : item.Params[0].Field[0].$.name;
            } else {
                this.setParam(template, item);
            }
            values.push(template);
        });

        if (isCollapse) {
            this.collapseEditor();
        }

        // need settimeout when collapse the editor
        setTimeout(() => {
            this.dispatchChanges(values);
        }, 100);
    }

    onSelectedRow(row) {
        /** uncomment to support multi select **/
        /** this.highlightedRows.push(row) **/
        this.highlightedRows = [row];
        if (this.expandedElement !== row) {
            this.expandedElement = null;
        }
    }

    dispatchChanges(values) {
        this.dispatchSceneItemChanges({
            sceneId: this.sceneId,
            sceneItemId: this.sceneItem.$.id,
            values: [{
                key: 'EventCommands',
                value: [{
                    EventCommand: values
                }],
                storePath: `Data[0]`
            }]
        });
        this.cd.markForCheck();
    }

    override initRegisterDynamic() {
        this.uiConfigInit = [];
    }

    override initApplyStoreToDynamic(applyStoreToDynamic: boolean = false) {
        super.initApplyStoreToDynamic(applyStoreToDynamic);
    }

    override componentInit() {
        super.componentInit();
    }

    setParam(value: ISendEvents, v) {
        if (value.Params[0].Param) {
            value.Params[0].Param[0].$.broadcast = v.Params?.[0]?.Param?.[0]?.$?.broadcast;
        }
        if (value.Params[0].Page) {
            value.Params[0].Page[0].$.name = v?.Params?.[0]?.Page?.[0]?.$?.name;
        }
        if (value.Params[0].Field) {
            value.Params[0].Field[0].$.name = v.Params?.[0]?.Field?.[0]?.$?.name;
        }
    }

    getParam(value: ISendEvents) {
        if (value.Params[0].Param) {
            return value.Params?.[0]?.Param?.[0]?.$?.broadcast;
        }
        if (value.Params[0].Page) {
            return value?.Params?.[0]?.Page?.[0]?.$?.name;
        }
        if (value.Params[0].Field) {
            return value?.Params?.[0]?.Field?.[0]?.$?.name;
        }
    }

    override componentStoreUpdates() {
        super.componentStoreUpdates();
        this.updateEvents(null);
        this.cd.markForCheck();
    }

    updateEvents(i_from) {
        this.onCustomizeTableDisplayColumns();
        this.dataSource = [];
        this.highlightedRows = [];
        const events = [];

        this.sceneItem.Data[0]?.EventCommands?.[0]?.EventCommand?.forEach((v, ind) => {
            const value = this.getSendEventTemplate();
            value.$.from = i_from ? i_from : v.$.from;
            events.push(value.$.from);
            value.$.command = v.$.command;
            value.$.zone = v.$.zone;
            this.setParam(value, v);
            if (this.expandedIndex === ind) {
                this.expandedElement = value;
            }
            this.dataSource.push(value);
        });
        this.dispatchEventsAll(events);
    }

    @Memoizee({})
    commandName(command) {
        const cmd = this.optionsLib.find((v) => v.value == command);
        return cmd ? cmd.name : '';
    }

    // Collapsed 'select' cell label. openPopup stores a scene id in Page.name — resolve it to the scene's
    // display name; every other command keeps showing the raw Page.name (e.g. selectPage's page value).
    selectDisplay(element) {
        const name = element?.Params?.[0]?.Page?.[0]?.$?.name;
        if (element?.$?.command === 'openPopup') {
            const s = this.sceneOptions.find((o) => String(o.value) === String(name));
            return s ? s.label : name;
        }
        return name;
    }

    expandEditor(element, index) {
        this.expandedElement = element;
        this.expandedIndex = index;
    }

    collapseEditor() {
        this.expandedElement = null;
        this.expandedIndex = null;
    }

    openEventsExplorerDialog(e) {
        const data: IEventBlock = {
            eventName: e.name,
            eventBlockId: this.sceneItemId,
            blockName: this.sceneItem.$.label,
            eventSceneId: this.sceneId,
            eventType: '',
            sceneName: ''
        };
        const dialogRefVal = this.dialog.open(DialogEventsExplorerComponent, {
            height: '100%',
            data
        });
        this.cod(
            dialogRefVal.afterClosed()
                .pipe(take(1))
                .subscribe((result: IEventBlock) => {
                    if (!result) return;
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    const values: ISendEvents[] = [];
                    const selected = this.highlightedRows.length === 0 ? this.dataSource[this.dataSource.length - 1] : this.highlightedRows[0];
                    this.dataSource.forEach((item: ISendEvents, index) => {
                        const value = this.getSendEventTemplate();
                        if (selected === item) {
                            value.$.from = result.eventName;
                        } else {
                            value.$.from = item.$.from;
                        }
                        value.$.command = item.$.command;
                        value.$.zone = item.$.zone;
                        this.setParam(value, item);
                        values.push(value);
                    });
                    setTimeout(() => {
                        this.dispatchChanges(values);
                    }, 100);
                    this.updateEvents(result.eventName);
                })
        );
    }

    override submit(formValues) {
        // formValues = removeUndefNulls(formValues);
        return super.submit(formValues);
    }
}


